import { Icon, Typography } from '@mui/material'
import { getDatasetsInItems, getReturnIdsFromDatasetInstruction } from 'helpers/dataset.helper/dataset.helper'
import { clone } from 'helpers/global.helper/global.helper'
import { memo, ReactElement, startTransition, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { chartStore, generateNewGroupId, registerChart, setItemsToGroupId, unregisterChart } from 'store/chart/chart'
import globalStore from 'store/global/global'
import { resetZoom } from 'store/zoom/zoom'
import { dynamicClassName } from 'styles/helper'
import { LoadingPlaceholderContainer } from 'ui/atoms'
import HeightAdjuster from 'ui/atoms/HeightAdjuster/HeightAdjuster'
import ExportDatasetsMenu from 'ui/components/ExportDatasetsMenu/ExportDatasetsMenu'
import TooltipIconButton from 'ui/components/TooltipIconButton/TooltipIconButton'
import Title from 'ui/molecules/Title/Title'
import {
  getComponentHeight,
  registerAppendPropsWithDatasets,
  registerComponentHeight,
  registerDefaultProps,
  registerUiConfigComponent,
} from 'ui/uiConfig/factory'
import { useSnapshot } from 'valtio'
import DatasetTable from '../DatasetTable/DatasetTable'
import { DatasetTableItem } from '../DatasetTable/DatasetTable.types'
import styles from './Chart.module.less'
import { ChartItem, ChartJS, ChartProps } from './Chart.types'
import ChartLegend from './ChartLegend/ChartLegend'
import { DEFAULT_CONFIG, DEFAULT_HEIGHT } from './chart.constants'
import {
  downloadChartAsImage,
  filterVisibleItemsInChart,
  getConfig,
  printChart,
  serializeChartConfig,
  setChartDatasetVisibility,
  setEmptyYAxisToHidden,
} from './chart.helper'
import ReactChartJS2 from './chart.init'
import FilterButtons from './FilterButtons/FilterButtons'
import { updateChartConfigWithYAxisLimits } from './helpers/syncYAxisPlugin'

const defaultProps: Omit<ChartProps, `datasets` | `children`> = {
  base_items: {
    all: false,
  },
  items: [
    {
      title: ``,
      data_id: ``,
      color: `$color-1`,
      unit: ``,
      fill: false,
      dashed: false,
      order: 0,
      tooltip_do_not_show_zero_values: true,
      stack_group: undefined,
      decimals: 1,
      y_axis_id: `y`,
    },
  ],
  config: DEFAULT_CONFIG,
  dataset_table: { default: false, display: false, show_only_value_in_future: true, initial_page_size: 25 },
  title: ``,
}

function appendPropsWithDataset(datasetInstruction: DatasetInstruction, previousProps: ChartProps): ChartProps {
  const returnIds = getReturnIdsFromDatasetInstruction(datasetInstruction)
  const newItems: ChartItem[] = returnIds.map((returnId) => ({
    title: returnId.split(`.`).pop() || ``,
    data_id: returnId,
    color: `$color-1`,
    unit: ``,
    fill: false,
    dashed: false,
    tooltip_do_not_show_zero_values: true,
    order: 0,
    stack_group: undefined,
    decimals: 1,
    y_axis_id: `y`,
  }))

  return {
    ...previousProps,
    items: [...(previousProps?.items || []), ...newItems],
    config: {
      ...previousProps?.config,
    },
  }
}
registerUiConfigComponent('chart', Chart)
registerAppendPropsWithDatasets('chart', appendPropsWithDataset)
registerDefaultProps('chart', defaultProps)
registerComponentHeight('chart', DEFAULT_HEIGHT)

function Chart({
  uid,
  items,
  config,
  datasets,
  title,
  filter_items,
  dataset_table,
  height: defaultHeight,
  hide_legend,
  legendKey,
  maxWidth,
  digitalTwinTranslations,
  settingInfo,
  excel_fill_method,
}: ChartProps): ReactElement {
  const { t } = useTranslation()

  // Used to be able to show a placeholder until we are ready to render the component.
  // This makes it possible to show a placeholder for all graphs before they render,
  // instead of the whole page being blank until all graphs are ready.
  const [showRenderPlaceholder, setShowRenderPlaceholder] = useState(true)

  useEffect(() => {
    // Wait until after first paint to start rendering the chart
    startTransition(() => {
      setShowRenderPlaceholder(false)
    })
  }, [])

  const chartSnap = useSnapshot(chartStore)
  const globalSnap = useSnapshot(globalStore)
  const [chart, setChart] = useState<ChartJS>()
  const [showDatasetTableUnderChart, setShowDatasetTableUnderChart] = useState<boolean>(
    () => (dataset_table?.default && dataset_table?.display) ?? false
  )
  const [height, setHeight] = useState(() => defaultHeight || DEFAULT_HEIGHT)

  const bodyWidth = globalSnap.bodyWidth

  // Memoize the effective config to avoid mutating props and recalculating on every render
  const effectiveConfig = useMemo(() => {
    const clonedConfig = clone(config || DEFAULT_CONFIG)
    if (hide_legend && clonedConfig?.options?.plugins?.chartLegend?.display) {
      clonedConfig.options.plugins.chartLegend.display = false
    }
    return clonedConfig
  }, [config, hide_legend])

  // Stable groupId - only regenerate when the configured groupId or shared setting changes
  const configuredGroupId = effectiveConfig?.options?.plugins?.chartLegend?.groupId
  const isShared = effectiveConfig?.options?.plugins?.chartLegend?.shared
  const groupIdRef = useRef<string | null>(null)

  const groupId = useMemo(() => {
    if (!configuredGroupId || !isShared) {
      // Only generate a new ID if we don't have one yet
      if (!groupIdRef.current) {
        groupIdRef.current = generateNewGroupId()
      }
      return groupIdRef.current
    }
    return configuredGroupId
  }, [configuredGroupId, isShared])

  const { filteredItemsForDatasetTable, datasetsInItemsForExport, datasetNames } = useMemo(() => {
    const hiddenReturnIds = chartSnap.groupIdHiddenReturnIds[groupId]
    const filteredItemsForDatasetTable = filter_items
      ? items.filter((item) => !hiddenReturnIds?.has(item.data_id))
      : items.filter((item) => !(item.show_only_in_chart || item.do_not_show_item))

    const itemsForExport = items.filter((item) => !item.do_not_show_item)
    const datasetsInItemsForExport = getDatasetsInItems(itemsForExport, datasets)
    const datasetNames = itemsForExport.reduce((acc, item) => ({ ...acc, [item.data_id]: item.title }), {})

    return { filteredItemsForDatasetTable, datasetsInItemsForExport, datasetNames }
  }, [chartSnap.groupIdHiddenReturnIds[groupId], datasets, filter_items, groupId, items])

  // Compute chart config, visibility states, and emptiness in a single useMemo to avoid multiple re-renders
  const { chartConfig, showLegend, hideExportButton, legendClass, chartIsEmpty } = useMemo(() => {
    const itemsToShowInChart = filterVisibleItemsInChart(items)

    let mergedConfig = getConfig(itemsToShowInChart, datasets, clone(effectiveConfig), filter_items)
    mergedConfig = setChartDatasetVisibility(
      mergedConfig,
      (chartSnap.groupIdHiddenReturnIds[groupId] || new Set()) as Set<string>
    )

    mergedConfig = setEmptyYAxisToHidden(mergedConfig)

    const chartIsEmpty = mergedConfig.data.datasets.every((d: any) => d.hidden === true)

    mergedConfig = updateChartConfigWithYAxisLimits(mergedConfig, chartSnap.groupIdAxisLimits[groupId])

    const serializedConfig = serializeChartConfig(mergedConfig)
    const showLegend = mergedConfig.options.plugins?.chartLegend?.display !== false
    const hideExportButton = mergedConfig.options.plugins?.exportButton?.display === false
    const legendClass = showLegend ? `chartLegend ${styles.Chart_Legend}` : ``

    return { chartConfig: serializedConfig, showLegend, hideExportButton, legendClass, chartIsEmpty }
  }, [
    datasets,
    items,
    effectiveConfig,
    filter_items,
    chartSnap.groupIdHiddenReturnIds[groupId],
    groupId,
    chartSnap.groupIdAxisLimits[groupId],
  ])

  useEffect(() => {
    if (!chart) {
      return
    }

    chartStore.chartIdToGroupId[chart.id] = groupId

    registerChart(groupId, chart)

    return () => {
      unregisterChart(groupId, chart)
    }
  }, [groupId, chart])

  useEffect(() => {
    setItemsToGroupId(groupId, filterVisibleItemsInChart(items), datasets)
  }, [groupId, items, datasets])

  /*
  [2024-08-07] Niklas Andreasson:
  The upgrade to React 18 unveiled a bug in ChartJS where the chart stacking
  won't work consistently wihout an additional update after the datasets have been changed.
  Related to https://app-eu.wrike.com/open.htm?id=1467779737. This is intended as a
  temporary fix until the issue is resolved in ChartJS or we've provided the
  stacked scale option for all of our chart configs.

  [2026-01-08] Extended to React 19: Chart needs to be explicitly updated when config changes
  to reflect visibility changes from filter buttons and legend toggles.
  */
  useEffect(() => {
    const datasetsLength = chartConfig?.data?.datasets?.length || 0
    if (chart && datasetsLength > 0) {
      chart.data = chartConfig.data
      chart.options = chartConfig.options
      chart.update()
    }
  }, [chart, chartConfig])

  const increaseChartHeight = useCallback(() => {
    setHeight((h) => h + 50)
  }, [])

  const decreaseChartHeight = useCallback(() => {
    setHeight((h) => (h < 200 ? h : h - 50))
  }, [])

  const chartRefCallback = (instance: ChartJS | null | undefined) => setChart(instance ?? undefined)

  // Show placeholder until we are ready to render the component
  if (showRenderPlaceholder) {
    return <LoadingPlaceholderContainer height={getComponentHeight('chart')} />
  }

  return (
    <div className={styles.Chart} style={{ maxWidth: maxWidth ? maxWidth : `${bodyWidth - 30}px` }}>
      {title && <Title title={title} size={`small`} />}

      {!!filter_items?.length && <FilterButtons filterItems={filter_items ?? []} groupId={groupId} />}

      <div className={legendClass}>
        {showLegend && (
          <ChartLegend
            groupId={groupId}
            persistKey={legendKey}
            digitalTwinTranslations={digitalTwinTranslations}
            settingInfo={settingInfo}
          />
        )}

        <div className={styles.Chart_Legend_ExportAndZoom__right}>
          <TooltipIconButton
            tooltip={t('Increase chart height')}
            onClick={increaseChartHeight}
            icon={<Icon color="primary" className="far fa-plus" fontSize="small" />}
          />
          <TooltipIconButton
            tooltip={t('Decrease chart height')}
            onClick={decreaseChartHeight}
            icon={<Icon color="primary" className="far fa-minus" fontSize="small" />}
          />

          {!hideExportButton && (
            <ExportDatasetsMenu
              datasets={datasetsInItemsForExport}
              datasetNames={datasetNames}
              excelFillMethod={excel_fill_method}
              onImageExport={() => chart && downloadChartAsImage(chart)}
              onPrint={() => chart && printChart(chart)}
            />
          )}

          {dataset_table?.display && (
            <TooltipIconButton
              tooltip={
                showDatasetTableUnderChart
                  ? t('Hide production plan values in table')
                  : t(`Show production plan values in table`)
              }
              onClick={() => setShowDatasetTableUnderChart(!showDatasetTableUnderChart)}
              icon={
                <Icon
                  color="primary"
                  className={
                    showDatasetTableUnderChart
                      ? styles.ExportDatasetsMenu_icon + ` far fa-th-list`
                      : styles.ExportDatasetsMenu_icon + ` fas fa-th-list`
                  }
                  fontSize="small"
                />
              }
            />
          )}

          {showLegend && (
            <TooltipIconButton
              tooltip={t(`Reset zoom`)}
              onClick={() => resetZoom()}
              icon={
                <Icon
                  color="primary"
                  className={`${styles.ExportDatasetsMenu_icon} fas fa-search-minus`}
                  fontSize="small"
                />
              }
            />
          )}
        </div>
      </div>

      <div
        className={dynamicClassName({
          [styles.Chart_Canvas]: true,
          [styles.Chart_Canvas__empty]: chartIsEmpty,
        })}
        style={{ height }}
      >
        <ReactChartJS2
          ref={chartRefCallback}
          type={chartConfig.type || 'line'}
          data={chartConfig.data || { datasets: [] }}
          options={chartConfig.options || {}}
          plugins={chartConfig.plugins || []}
        />
        {chartIsEmpty && (
          <Typography variant="caption" className={styles.Chart_MissingData}>
            {t('No records to display')}
          </Typography>
        )}
      </div>

      {dataset_table && showDatasetTableUnderChart && (
        <div className={styles.Chart_Table}>
          <div className={styles.Chart_Table__table}>
            <DatasetTable
              uid={uid}
              items={filteredItemsForDatasetTable as DatasetTableItem[]}
              datasets={datasets}
              children={undefined}
              title={dataset_table.title ?? ''}
              do_not_show_zero_or_null_values_in_table={dataset_table.do_not_show_zero_or_null_values_in_table ?? false}
              do_not_show_download_button={dataset_table.do_not_show_download_button ?? true}
              do_not_show_sum_or_average_row={dataset_table.do_not_show_sum_or_average_row ?? false}
              do_not_show_min_max_row={dataset_table.do_not_show_min_max_row ?? false}
              initial_page_size={dataset_table.initial_page_size ?? 24}
              show_only_value_in_future={dataset_table.show_only_value_in_future ?? false}
              do_not_show_top_toolbar={dataset_table.do_not_show_top_toolbar ?? false}
              use_column_color_from_items={dataset_table.use_column_color_from_items ?? true}
              show_initial_page_with_current_day={dataset_table.show_initial_page_with_current_day ?? false}
              show_initial_page_with_current_optimization_hour={
                dataset_table.show_initial_page_with_current_optimization_hour ?? true
              }
              sorting_function={dataset_table.sorting_function ?? 'chart_items'}
              footer_calculates_per_page={dataset_table.footer_calculates_per_page ?? false}
              sticky_table_header={dataset_table.sticky_table_header ?? true}
              enableCellRangeSelectionForClipboard={dataset_table.enableCellRangeSelectionForClipboard ?? false}
              enableColumnActions={false}
              expandableHours
            />
          </div>
        </div>
      )}

      <HeightAdjuster height={height} minHeight={200} maxHeight={1000} onChange={setHeight} />
    </div>
  )
}

export default memo(Chart)
